// features/cx/outstanding/api.ts

import { ApiEndpoints } from '@shared/api/api-endpoints';
import { communicationApiClient, cxApiClient } from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  OutstandingBillsFilters,
  OutstandingSummary,
  CreatePaymentAgreementPayload,
  PaymentAgreement,
  PaymentAgreementDetail,
  PaymentAgreementFilters,
  OutstandingBill,
  Account
} from './types';
import { PaginatedResponse } from '@core/types';

// API Functions
export const outstandingApi = {
  // Outstanding Bills List
  getOutstandingBills: async (filters: OutstandingBillsFilters): Promise<{
    pageNumber: number;
    count: number;
    next: string | null;
    previous: string | null;
    result: OutstandingBill[];
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('cx', 'outstanding-bills', (query) => {
      if (filters.remoteUtilityId) query.push('remote_utility_id', filters.remoteUtilityId.toString());
      if (filters.page) query.push('page', filters.page.toString());
      if (filters.limit) query.push('limit', filters.limit.toString());
      if (filters.searchData) query.push('search_data', filters.searchData);
      if (filters.isPagination) query.push('is_pagination', '1');
      return query;
    });
    
    const response = await cxApiClient.get<PaginatedResponse<OutstandingBill>>(url);
    return transformJsonToObject<{
      pageNumber: number;
      count: number;
      next: string | null;
      previous: string | null;
      result: OutstandingBill[];
    }>(response.data);
  },

  exportOutstandingBills: async (filters: OutstandingBillsFilters): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('cx', 'outstanding-bills', (query) => {
      if (filters.remoteUtilityId) query.push('remote_utility_id', filters.remoteUtilityId.toString());
      if (filters.page) query.push('page', filters.page.toString());
      if (filters.limit) query.push('limit', filters.limit.toString());
      if (filters.searchData) query.push('search_data', filters.searchData);
      if (filters.isPagination) query.push('is_pagination', '1');
      query.push('export_format', 'csv');
      return query;
    });
    
    const response = await cxApiClient.get(url);
    
    return response.data;
  },

  // Outstanding Summary
  getOutstandingSummary: async (remoteUtilityId: number): Promise<OutstandingSummary> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('cx', 'outstanding/summary', (query) => {
      query.push('remote_utility_id', remoteUtilityId.toString());
      return query;
    });
    
    const response = await cxApiClient.get(url);
    return transformJsonToObject<OutstandingSummary>(response.data.result);
  },

  // Create Payment Agreement
  createPaymentAgreement: async (payload: CreatePaymentAgreementPayload): Promise<PaymentAgreement> => {
    const url = ApiEndpoints.createUrl('cx', 'payment-agreement');
    const response = await cxApiClient.post(url, payload);
    return transformJsonToObject<PaymentAgreement>(response.data);
  },

  // Get Payment Agreement Detail
  getPaymentAgreementDetail: async (params: {
    remoteUtilityId: number;
    remoteBillId: number;
  }): Promise<{ result: PaymentAgreementDetail }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('cx', 'payment-agreement/detail', (query) => {
      query.push('remote_utility_id', params.remoteUtilityId.toString());
      query.push('remote_bill_id', params.remoteBillId.toString());
      return query;
    });
    
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: PaymentAgreementDetail }>(response.data);
  },

  // Get Payment Agreements List (additional endpoint for listing all agreements)
  getPaymentAgreements: async (filters: PaymentAgreementFilters): Promise<{
    pageNumber: number;
    count: number;
    next: string | null;
    previous: string | null;
    result: PaymentAgreement[];
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('cx', 'payment-agreement', (query) => {
      if (filters.remoteUtilityId) query.push('remote_utility_id', filters.remoteUtilityId.toString());
      if (filters.remoteBillId) query.push('remote_bill_id', filters.remoteBillId.toString());
      if (filters.consumerId) query.push('consumer_id', filters.consumerId.toString());
      if (filters.status) query.push('status', filters.status);
      if (filters.page) query.push('page', filters.page.toString());
      if (filters.limit) query.push('limit', filters.limit.toString());
      return query;
    });
    
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{
      pageNumber: number;
      count: number;
      next: string | null;
      previous: string | null;
      result: PaymentAgreement[];
    }>(response.data);
  },

  // Update Payment Agreement (if needed)
  updatePaymentAgreement: async (agreementId: number, payload: Partial<CreatePaymentAgreementPayload>): Promise<PaymentAgreement> => {
    const url = ApiEndpoints.createUrlWithPathVariables('cx', 'payment-agreement', [agreementId.toString()]);
    const response = await cxApiClient.put(url, payload);
    return transformJsonToObject<PaymentAgreement>(response.data);
  },

  deletePaymentAgreement: async (params: {
    agreementId: number;
  }): Promise<{ result: any }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('cx', `payment-agreement/${params.agreementId}`);
    const response = await cxApiClient.put(url,{
      status: 3,
    });
    return transformJsonToObject<{ result: any }>(response.data);
  },


  getAccountDetails: async (params: {
    remoteUtilityId: number;
    consumerNumber: string;
  }): Promise<{ result: any }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('cx', 'consumer-bill', (query) => {
      query.push('remote_utility_id', params.remoteUtilityId.toString());
      query.push('consumer_number', params.consumerNumber);
      query.push('fetch_latest', 'True');
      query.push('address_required', 'True');
      query.push('is_category_required', 'True');
      return query;
    });
    
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: any }>(response.data);
  },

  sendReminder: async (payload: any): Promise<{ result: any }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('communication', 'direct-message');
    
    const response = await communicationApiClient.post(url, payload);
    return transformJsonToObject<{ result: any }>(response.data);
  },

  
};